import { z } from "zod";

type FormMode = "register" | "edit" | "admin";

export const createExpo48Schema = (mode: FormMode = "register", requireCouponFields = false) => {
  const isRegisterMode = mode === "register";
  const isEditMode = mode === "edit";

  return z
    .object({
      name: z
        .string()
        .trim()
        .min(2, "Name must be at least 2 characters / ชื่อต้องมีอย่างน้อย 2 ตัวอักษร")
        .max(100, "Name is too long / ชื่อยาวเกินไป"),
      surname: z
        .string()
        .trim()
        .min(2, "Surname must be at least 2 characters / นามสกุลต้องมีอย่างน้อย 2 ตัวอักษร")
        .max(100, "Surname is too long / นามสกุลยาวเกินไป"),
      phone: z
        .string()
        .trim()
        .transform((val) => val.replace(/\D/g, "")) // Strip formatting before validation
        .pipe(
          z
            .string()
            .regex(
              /^0\d{9}$/,
              "Phone must be 10 digits starting with 0 / เบอร์โทรต้องเป็นตัวเลข 10 หลัก ขึ้นต้นด้วย 0"
            )
        ),
      email: z
        .string()
        .trim()
        .email("Invalid email format / รูปแบบอีเมลไม่ถูกต้อง")
        .max(255, "Email is too long / อีเมลยาวเกินไป"),
      age:
        isRegisterMode || isEditMode
          ? z
              .string()
              .min(1, "Age is required / กรุณาระบุอายุ")
              .refine(
                (val) => {
                  const num = Number(val);
                  return !isNaN(num) && num >= 1 && num <= 120;
                },
                { message: "Age must be between 1-120 / อายุต้องอยู่ระหว่าง 1-120" }
              )
          : z
              .string()
              .optional()
              .refine(
                (val) => {
                  if (!val || val.trim() === "") return true;
                  const num = Number(val);
                  return !isNaN(num) && num >= 1 && num <= 120;
                },
                { message: "Age must be between 1-120 / อายุต้องอยู่ระหว่าง 1-120" }
              ),
      gender:
        isRegisterMode || isEditMode
          ? z.string().min(1, "Gender is required / กรุณาระบุเพศ")
          : z.string().trim().optional(),
      interests: isRegisterMode
        ? z
            .array(z.string())
            .min(1, "Please select at least one interest / กรุณาเลือกความสนใจอย่างน้อย 1 รายการ")
        : z.array(z.string()),
      interestOther: z.string().trim().optional(),
      banks: isRegisterMode
        ? z
            .array(z.string())
            .min(1, "Please select at least one bank / กรุณาเลือกธนาคารอย่างน้อย 1 แห่ง")
        : z.array(z.string()),
      bankOther: z.string().trim().optional(),
      propertyCharacteristics: isRegisterMode
        ? z.string().min(1, "Property characteristics is required / กรุณาระบุลักษณะที่อยู่อาศัย")
        : z.string().trim().optional(),
      priceRange: isRegisterMode
        ? z.string().min(1, "Price range is required / กรุณาระบุช่วงราคา")
        : z.string().trim().optional(),
      locationData: isRegisterMode
        ? z.object({
            province: z.string().min(1, "Province is required / กรุณาเลือกจังหวัด"),
            district: z.string().optional(),
          })
        : z
            .object({
              province: z.string().optional(),
              district: z.string().optional(),
            })
            .optional(),
      purchaseYear: isRegisterMode
        ? z.string().min(1, "Purchase year is required / กรุณาระบุปีที่วางแผนจะซื้อ")
        : z.string().trim().optional(),
      // Property purchases as an array
      purchases: requireCouponFields
        ? z
            .array(
              z.object({
                id: z.number().optional(),
                purchasePrice: z.string().min(1, "Purchase price is required / กรุณาระบุราคาซื้อ"),
                developer: z.string().min(1, "Developer is required / กรุณาระบุผู้พัฒนา"),
                fullAddress: z
                  .string()
                  .trim()
                  .min(1, "Full address is required / กรุณาระบุที่อยู่เต็ม"),
                purchasedPropertyType: z
                  .string()
                  .min(
                    1,
                    "Purchased property type is required / กรุณาระบุประเภทอสังหาริมทรัพย์ที่ซื้อ"
                  ),
                purchasedPropertyCharacteristics: z
                  .string()
                  .min(
                    1,
                    "Purchased property characteristics is required / กรุณาระบุลักษณะที่อยู่อาศัยที่ซื้อ"
                  ),
              })
            )
            .min(1, "At least one purchase is required / กรุณาเพิ่มข้อมูลการซื้ออย่างน้อย 1 รายการ")
        : z
            .array(
              z.object({
                id: z.number().optional(),
                purchasePrice: z.string().optional(),
                developer: z.string().optional(),
                fullAddress: z.string().optional(),
                purchasedPropertyType: z.string().optional(),
                purchasedPropertyCharacteristics: z.string().optional(),
              })
            )
            .optional(),
    })
    .superRefine((data, ctx) => {
      // Validate that if "other" is selected, interestOther must be filled
      if (
        data.interests.includes("other") &&
        (!data.interestOther || data.interestOther.trim() === "")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please specify other interest / กรุณาระบุความสนใจอื่นๆ",
          path: ["interestOther"],
        });
      }

      // Validate that if "อื่นๆ / Other" is selected for banks, bankOther must be filled
      if (
        data.banks.includes("อื่นๆ / Other") &&
        (!data.bankOther || data.bankOther.trim() === "")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please specify other bank / กรุณาระบุธนาคารอื่นๆ",
          path: ["bankOther"],
        });
      }

      // Validate that if province is Bangkok in register mode, district is required
      if (isRegisterMode && data.locationData && data.locationData.province) {
        // Extract Thai name from bilingual format
        const extractThaiName = (str: string) => {
          const match = str.match(/^(.+?)\s*\(/);
          return match ? match[1].trim() : str;
        };

        const thaiProvince = extractThaiName(data.locationData.province);
        if (
          thaiProvince === "กรุงเทพมหานคร" &&
          (!data.locationData.district || data.locationData.district.trim() === "")
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "District is required for Bangkok / กรุณาเลือกเขต",
            path: ["locationData", "district"],
          });
        }
      }
    });
};

export type Expo48FormData = z.infer<ReturnType<typeof createExpo48Schema>>;
export type Expo48FormInput = z.input<ReturnType<typeof createExpo48Schema>>;
